import { useScrollAnimationSection } from '../../../hooks/useScrollAnimation';

interface TrustSectionProps {
  onOpenModal: () => void;
}

const trustPoints = [
  {
    icon: 'ri-shield-check-line',
    title: '中立的な情報',
    desc: '特定の不動産会社・ハウスメーカーとは一切無関係。偏りのない情報をお届けします。',
  },
  {
    icon: 'ri-book-read-line',
    title: '初心者目線',
    desc: '専門用語はすべて平易な言葉で解説。住宅知識ゼロでも理解できる内容です。',
  },
  {
    icon: 'ri-update-line',
    title: '定期的に更新',
    desc: '法改正・金利動向・最新事例に合わせて記事を定期的にアップデートしています。',
  },
];

export default function TrustSection({ onOpenModal }: TrustSectionProps) {
  const sectionRef = useScrollAnimationSection();

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#FAFAF8]"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left - Image */}
          <div className="fade-in-up order-2 lg:order-1">
            <div className="relative w-full h-[480px] rounded-3xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/dede5cf315b5aacaeca2b24d0b5200fe.jpg"
                alt="信頼できるマイホームイメージ"
                className="w-full h-full object-cover object-top"
              />
              {/* Floating Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#E8F0E6] flex-shrink-0">
                    <i className="ri-verified-badge-line text-[#8FA882] text-lg" />
                  </div>
                  <div>
                    <p className="text-[#2C2C2C] text-sm font-semibold">中立・公正な情報サイト</p>
                    <p className="text-[#6B6B6B] text-xs mt-0.5">特定企業とは一切提携なし。読者の利益を最優先に情報を提供します。</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Text */}
          <div className="order-1 lg:order-2">
            <div className="fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E8DDD0] text-[#8A7040] text-xs font-semibold tracking-wide mb-5">
                <i className="ri-information-line" />
                このサイトについて
              </div>
            </div>

            <h2 className="fade-in-up text-3xl md:text-4xl font-bold text-[#2C2C2C] leading-tight mb-4">
              初心者の不安に、<br />
              <span className="text-[#8FA882]">とことん寄り添います</span>
            </h2>

            <p className="fade-in-up text-[#6B6B6B] text-base leading-relaxed mb-8">
              「はじめての家づくりガイド」は、住宅購入を初めて検討するあなたのために作られたブログサイトです。
              難しい専門用語は使わず、やさしい言葉で、正確な情報をお届けします。
              住宅業界の複雑さに戸惑うことなく、自信を持って家づくりを進められるよう、全力でサポートします。
            </p>

            {/* Trust Points */}
            <div className="space-y-4">
              {trustPoints.map((point, i) => (
                <div
                  key={point.title}
                  className="fade-in-up flex items-start gap-4 p-4 rounded-xl bg-[#F5F1ED] hover:bg-[#EDE8E0] transition-colors duration-200"
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#8FA882] flex-shrink-0">
                    <i className={`${point.icon} text-white text-base`} />
                  </div>
                  <div>
                    <p className="text-[#2C2C2C] text-sm font-semibold">{point.title}</p>
                    <p className="text-[#6B6B6B] text-xs mt-1 leading-relaxed">{point.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quote */}
            <div className="fade-in-up mt-8 pl-5 border-l-4 border-[#8FA882]">
              <p className="text-[#6B6B6B] text-sm leading-relaxed italic">
                「難しそう」「騙されたくない」という不安は、正しい知識があれば解消できます。
                このサイトがあなたの家づくりの第一歩を、安心して踏み出すためのきっかけになれれば嬉しいです。
              </p>
              <p className="text-[#8FA882] text-xs font-semibold mt-2">— はじめての家づくりガイド 編集部</p>
            </div>

            {/* CTA Banner */}
            <div className="fade-in-up mt-8 p-5 rounded-2xl bg-[#E8F0E6] border border-[#C8D8C0]">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#8FA882] flex-shrink-0">
                    <i className="ri-home-line text-white text-base" />
                  </div>
                  <div>
                    <p className="text-[#2C2C2C] text-sm font-bold">鳥取エリアの物件情報を無料でお届け</p>
                    <p className="text-[#6B6B6B] text-xs mt-0.5">間取り・価格・工務店情報など、まとめて資料請求できます。</p>
                  </div>
                </div>
                <button
                  onClick={onOpenModal}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A96A] text-white text-sm font-bold hover:bg-[#C09050] transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                >
                  <i className="ri-file-list-3-line" />
                  無料で資料請求する
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
